import { getFlowDiagram } from "./flowApi";
import { renderFlowSvg } from "./flowSvg";
import { figureBodyHtml, renumberCaptions, setFigureBody } from "../rteCaptions";
import "./flowDiagram.css";

const inflight = new Map();

function figuresFor(id, { includeEditable = false } = {}) {
  const safe = window.CSS?.escape ? CSS.escape(String(id)) : String(id).replace(/"/g, "");
  return [...document.querySelectorAll(`figure.rte-flow[data-rte-flow-id="${safe}"]`)].filter((fig) => {
    if (includeEditable) return true;
    return !fig.closest('[contenteditable="true"]');
  });
}

export function paintFlowFigures(id, svg, rev) {
  figuresFor(id).forEach((fig) => {
    if (rev && fig.getAttribute("data-rte-flow-rev") === String(rev) && figureBodyHtml(fig) === svg) return;
    fig.classList.remove("rte-flow-missing");
    setFigureBody(fig, svg);
    if (rev) fig.setAttribute("data-rte-flow-rev", String(rev));
  });
  renumberCaptions(document.body);
}

export function markFlowMissing(id) {
  figuresFor(id).forEach((fig) => {
    if (fig.getAttribute("data-rte-flow-rev") === "missing") return;
    fig.classList.add("rte-flow-missing");
    setFigureBody(fig, '<p class="rte-flow-missing">Diagram unavailable</p>');
    fig.setAttribute("data-rte-flow-rev", "missing");
  });
}

function hydrateId(id) {
  if (inflight.has(id)) return inflight.get(id);
  const job = (async () => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const record = await getFlowDiagram(id);
      paintFlowFigures(id, renderFlowSvg(record), record.updatedAt || "");
    } catch (err) {
      if (err.status === 404) markFlowMissing(id);
    }
  })().finally(() => inflight.delete(id));
  inflight.set(id, job);
  return job;
}

export function hydrateFlowRoot(root) {
  if (!root?.querySelectorAll) return;
  const ids = new Set();
  root.querySelectorAll("figure.rte-flow[data-rte-flow-id]").forEach((fig) => {
    if (fig.closest('[contenteditable="true"]')) return;
    if (fig.getAttribute("data-rte-flow-rev")) return;
    const id = fig.getAttribute("data-rte-flow-id");
    if (id) ids.add(id);
  });
  ids.forEach((id) => {
    hydrateId(id);
  });
}

export function startFlowHydration() {
  if (typeof document === "undefined") return () => {};
  let timer = 0;
  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      hydrateFlowRoot(document.body);
      renumberCaptions(document.body);
    }, 60);
  };
  const onUpdated = (event) => {
    const { id, svg, updatedAt } = event.detail || {};
    if (!id || !svg) return;
    paintFlowFigures(id, svg, updatedAt || `local-${Date.now()}`);
  };
  const onRemoved = (event) => {
    const id = event.detail?.id;
    if (id) markFlowMissing(id);
  };
  schedule();
  const observer = new MutationObserver(schedule);
  observer.observe(document.body, { childList: true, subtree: true });
  window.addEventListener("rte-flow-updated", onUpdated);
  window.addEventListener("rte-flow-removed", onRemoved);
  return () => {
    observer.disconnect();
    window.removeEventListener("rte-flow-updated", onUpdated);
    window.removeEventListener("rte-flow-removed", onRemoved);
    window.clearTimeout(timer);
  };
}
